/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSellerTemplateData } from "./useSellerTemplateData";

/**
 * Checks if a field already has meaningful data
 * so template doesn't overwrite user edits or loaded calc.
 */
const hasData = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") {
    return Object.values(value).some(
      (v) => v != null && v !== "" && String(v).trim() !== "",
    );
  }
  return value != null && value !== "" && String(value).trim() !== "";
};

export const useUserTemplate = (mainId, userId, companyId, handlers = {}) => {
  const templateData = useSellerTemplateData(
    mainId && userId && companyId ? mainId : null,
    mainId && userId && companyId ? userId : null,
    mainId && userId && companyId ? companyId : null,
  );

  const [templateMissing, setTemplateMissing] = useState(false);

  // ✅ stable ref so the effect doesn't re-run from object identity changes
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // ✅ applyKey increments when you click “New Calc”
  const applyKey = handlers?.applyKey ?? 0;

  // ✅ this ensures template can apply again after reset
  const contextKey = useMemo(() => {
    if (!mainId || !userId || !companyId) return "";
    return `${mainId}-${userId}-${companyId}-apply:${applyKey}`;
  }, [mainId, userId, companyId, applyKey]);

  // ✅ prevents applying twice for same contextKey
  const appliedRef = useRef("");
  const lastBaseContextRef = useRef("");

  useEffect(() => {
    if (!templateData || !contextKey) return;
    const currentBaseContext = `${mainId}-${userId}-${companyId}`;
    if (templateData?.contextKey !== currentBaseContext) return;
    if (appliedRef.current === contextKey) return;

    const h = handlersRef.current;
    const baseContextKey = `${mainId}-${userId}-${companyId}`;
    const companyContextChanged =
      !!lastBaseContextRef.current &&
      lastBaseContextRef.current !== baseContextKey;

    // Guard: don’t apply when editing an existing calc
    if (h?.calcId) {
      appliedRef.current = contextKey;
      return;
    }

    // Guard: don’t apply while parent is populating data
    if (h?.isPopulatingRef?.current) {
      appliedRef.current = contextKey;
      return;
    }

    const setValue = h?.setValue;
    const getValues = h?.getValues;

    if (typeof setValue !== "function") {
      console.warn("useUserTemplate: missing setValue()");
      appliedRef.current = contextKey;
      return;
    }

    const { sellerSections, transferTax } = templateData || {};
    if (!sellerSections) {
      setTemplateMissing(true);
      appliedRef.current = contextKey;
      return;
    }

    // console.log("sellerSections", sellerSections);

    // optional “missing template” detection
    const emptyTemplate =
      !sellerSections.section1?.length &&
      !sellerSections.section2?.length &&
      !sellerSections.sellerOtherCosts?.length;

    setTemplateMissing(Boolean(emptyTemplate));

    /**
     * Apply array only if RHF field is empty
     * Prefer fieldArray.replace via provided handlers.
     */
    const setArrayIfEmpty = (fieldName, replaceFn, data) => {
      const existing =
        typeof getValues === "function" ? getValues(fieldName) : null;

      const incoming = data || [];
      const hasExisting = hasData(existing);

      if (!incoming.length && hasExisting) return;

      if (typeof replaceFn === "function") replaceFn(incoming);
      else setValue(fieldName, incoming);
    };

    // ✅ apply arrays
    setArrayIfEmpty(
      "sellerClosingCostMeta",
      h.replaceSellerClosingCost,
      sellerSections.section1,
    );
    setArrayIfEmpty(
      "closingCostMeta",
      h.replaceClosingCostsSection2,
      sellerSections.section2,
    );
    setArrayIfEmpty(
      "sellerOtherCostMeta",
      h.replaceSellerOtherCost,
      sellerSections.sellerOtherCosts,
    );
    setArrayIfEmpty(
      "otherCostMeta",
      h.replaceOtherCost,
      sellerSections.otherCosts,
    );
    setArrayIfEmpty(
      "compensationMeta",
      h.replaceCompensation,
      sellerSections.paymentsToBuyer,
    );

    /**
     * Apply scalar defaults only if empty
     */
    const setIfEmpty = (fieldName, defaultValue) => {
      const existing =
        typeof getValues === "function" ? getValues(fieldName) : null;
      if (!hasData(existing)) setValue(fieldName, defaultValue);
    };

    setIfEmpty("salesPrice", "0.00");
    setIfEmpty("preparedBy", "");
    setIfEmpty("preparedFor", "New Client");

    // Always set templateSellerOtherCosts (needed later for loan type switching)
    setValue(
      "templateSellerOtherCosts",
      sellerSections.templateSellerOtherCosts || {},
    );

    // Ensure loanInfoMeta exists (only if empty)
    const existingLoan =
      typeof getValues === "function" ? getValues("loanInfoMeta") : null;
    if (!hasData(existingLoan)) {
      setValue("loanInfoMeta", {
        buyersLoanType: "Conventional",
        conventionalPercent: "80.00",
        transferTaxRatePer: transferTax ?? "0.00",
        homeWarrantyAmount: "0.00",
        mortgageAmount: "0.00",
      });
    } else {
      // If it exists but transferTaxRatePer is empty, fill it (non-destructive)
      if (companyContextChanged || !hasData(existingLoan?.transferTaxRatePer)) {
        setValue("loanInfoMeta.transferTaxRatePer", transferTax ?? "0.00");
      }
    }

    lastBaseContextRef.current = baseContextKey;
    appliedRef.current = contextKey;

    const after = h?.onAfterTemplateApply;
    if (typeof after === "function") {
      queueMicrotask(() => {
        try {
          after();
        } catch (e) {
          console.warn("onAfterTemplateApply failed", e);
        }
      });
    }
  }, [templateData, contextKey]);

  return { templateMissing };
};
